import { collection, getDocs, setDoc, doc } from 'firebase/firestore';
import { db as firestore } from '../firebase/firebaseConfig';
import { implicitTitle } from './huntDomain';

/**
 * One-time import of a user's existing Number Hunter photos into Photo
 * Hunter. Both apps share the same Firebase project and the same
 * `users/{uid}/photos` collection path — Number Hunter writes a bare
 * `number` field with no `huntId`, so any doc with `number` and no `huntId`
 * is unambiguously a legacy Number Hunter photo, never one of Photo
 * Hunter's own. Purely additive: new photo docs (new ids) are created in
 * Photo Hunter's shape, reusing the existing thumbnailUrl (no image
 * duplication) — Number Hunter's own documents are never read for writing,
 * only read for copying, and are never modified or deleted.
 *
 * @returns {{ imported: number, hunt: object } | { imported: 0 }}
 */
export async function importFromNumberHunter({ user, state, upsertPhotoState, upsertHuntState }) {
  const snap = await getDocs(collection(firestore, 'users', user.uid, 'photos'));
  const legacyPhotos = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(p => p.number != null && !p.huntId);

  if (legacyPhotos.length === 0) {
    return { imported: 0 };
  }

  // Prefer an existing empty Numbers hunt (e.g. the auto-bootstrapped default
  // on a fresh account) over creating a redundant second one.
  const photoCountByHunt = new Map();
  for (const p of state.photos) {
    photoCountByHunt.set(p.huntId, (photoCountByHunt.get(p.huntId) ?? 0) + 1);
  }
  let targetHunt = state.hunts.find(
    h => h.type === 'numbers' && (photoCountByHunt.get(h.id) ?? 0) === 0
  );

  if (!targetHunt) {
    const now = new Date().toISOString();
    targetHunt = {
      id: crypto.randomUUID(),
      type: 'numbers',
      title: implicitTitle('numbers'),
      huntList: [],
      createdAt: now,
      updatedAt: now,
    };
    await upsertHuntState(targetHunt);
    await setDoc(doc(firestore, 'users', user.uid, 'hunts', targetHunt.id), targetHunt);
  }

  for (const legacy of legacyPhotos) {
    const photo = {
      id: crypto.randomUUID(),
      huntId: targetHunt.id,
      itemKey: String(legacy.number),
      location: legacy.location ?? '',
      filePath: legacy.filePath ?? null,
      thumbnailUrl: legacy.thumbnailUrl ?? '',
      thumbnailDataUrl: null,
      qualityScore: legacy.qualityScore ?? null,
      addedAt: legacy.addedAt ?? new Date().toISOString(),
      isPrimary: !!legacy.isPrimary,
    };
    await upsertPhotoState(photo);
    await setDoc(doc(firestore, 'users', user.uid, 'photos', photo.id), photo);
  }

  return { imported: legacyPhotos.length, hunt: targetHunt };
}
